import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/authStore';
import { settingsAPI } from '../lib/api';
import {
  LayoutDashboard,
  Users,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Moon,
  Sun,
  ChevronDown,
  HelpCircle,
  ChevronRight,
  FileCheck,
  Cog,
  Globe,
  Home,
  Headset
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import { Avatar, AvatarFallback } from './ui/avatar';

const THEME_COLORS_STORAGE_KEY = 'appThemeColors';
const DEFAULT_THEME_COLORS = {
  lightThemeColor: '#333333',
  darkThemeColor: '#4b5563',
};

// --- Tema rengi yardımcıları (seçilen hex -> CSS değişkenleri) ---
function hexToRgb(hex) {
  const h = (hex || '').replace('#', '').trim();
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const int = parseInt(full || '333333', 16);
  return { r: (int >> 16) & 255, g: (int >> 8) & 255, b: int & 255 };
}
function isLightColor(hex) {
  const { r, g, b } = hexToRgb(hex);
  return 0.299 * r + 0.587 * g + 0.114 * b > 160; // algısal parlaklık
}
function hexToHslTriplet(hex) {
  let { r, g, b } = hexToRgb(hex);
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max === min) { h = s = 0; }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function readThemeColorsFromStorage() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(THEME_COLORS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      lightThemeColor: parsed?.lightThemeColor || DEFAULT_THEME_COLORS.lightThemeColor,
      darkThemeColor: parsed?.darkThemeColor || DEFAULT_THEME_COLORS.darkThemeColor,
    };
  } catch {
    return null;
  }
}

function writeThemeColorsToStorage(colors) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(THEME_COLORS_STORAGE_KEY, JSON.stringify(colors));
  } catch {
    // ignore
  }
}

export const Layout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const navItems = [
    { path: '/', icon: Home, label: t('nav.dashboard') },
    { path: '/customers', icon: Users, label: t('nav.customers') },
    {
      path: '/contracts',
      icon: FileText,
      label: t('nav.contracts'),
      submenu: [
        { path: '/contracts', label: t('nav.allContracts') },
        { path: '/contracts/new', label: t('nav.addNewContract') },
      ]
    },
    {
      path: '/settings',
      icon: Settings,
      label: t('nav.settings'),
      submenu: [
        { path: '/settings/contracts', label: t('nav.contractSettings'), icon: FileCheck },
        { path: '/settings/application', label: t('nav.applicationSettings'), icon: Cog },
      ]
    },
    {
      path: '/support',
      icon: Headset,
      label: t('nav.support'),
    },
  ];

  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [contractsOpen, setContractsOpen] = useState(
    location.pathname.startsWith('/contracts')
  );
  const [settingsOpen, setSettingsOpen] = useState(
    location.pathname.startsWith('/settings')
  );
  const [supportOpen, setSupportOpen] = useState(
    location.pathname.startsWith('/support')
  );
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark');
    }
    return false;
  });

  const [themeSettings, setThemeSettings] = useState(() => {
    return readThemeColorsFromStorage() || DEFAULT_THEME_COLORS;
  });

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await settingsAPI.get();
        if (response.data) {
          const colors = {
            lightThemeColor: response.data.light_theme_color || '#333333',
            darkThemeColor: response.data.dark_theme_color || '#4b5563',
          };
          setThemeSettings(colors);
          writeThemeColorsToStorage(colors);
        }
      } catch (error) {
        console.error('Failed to load theme settings', error);
      }
    };
    loadSettings();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onThemeColorsUpdated = (e) => {
      const detail = e?.detail;
      if (!detail) return;
      const next = {
        lightThemeColor: detail.lightThemeColor || themeSettings.lightThemeColor,
        darkThemeColor: detail.darkThemeColor || themeSettings.darkThemeColor,
      };
      setThemeSettings(next);
      writeThemeColorsToStorage(next);
    };

    const onStorage = (e) => {
      if (e?.key !== THEME_COLORS_STORAGE_KEY) return;
      const fromStorage = readThemeColorsFromStorage();
      if (fromStorage) setThemeSettings(fromStorage);
    };

    window.addEventListener('appThemeColorsUpdated', onThemeColorsUpdated);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('appThemeColorsUpdated', onThemeColorsUpdated);
      window.removeEventListener('storage', onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentThemeColor = darkMode ? themeSettings.darkThemeColor : themeSettings.lightThemeColor;

  // Seçilen tema rengini gerçekten UI'a uygula (CSS değişkenleri):
  // --theme-accent (sidebar aktif, dashboard kartları) + shadcn --primary (primary butonlar).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const accent = currentThemeColor || '#333333';
    const light = isLightColor(accent);
    root.style.setProperty('--theme-accent', accent);
    root.style.setProperty('--theme-accent-foreground', light ? '#111111' : '#ffffff');
    root.style.setProperty('--primary', hexToHslTriplet(accent));
    root.style.setProperty('--primary-foreground', light ? '222 47% 11%' : '210 40% 98%');
  }, [currentThemeColor]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    document.documentElement.classList.toggle('dark');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const isContractsSection = location.pathname.startsWith('/contracts');
  const isSettingsSection = location.pathname.startsWith('/settings');
  const isSupportSection = location.pathname.startsWith('/support');

  const renderNavItem = (item) => {
    if (item.submenu) {
      let isOpen, setOpen, isSection;

      if (item.path === '/contracts') {
        isOpen = contractsOpen;
        setOpen = (open) => {
          setContractsOpen(open);
          if (open) {
            setSettingsOpen(false);
            setSupportOpen(false);
          }
        };
        isSection = isContractsSection;
      } else if (item.path === '/settings') {
        isOpen = settingsOpen;
        setOpen = (open) => {
          setSettingsOpen(open);
          if (open) {
            setContractsOpen(false);
            setSupportOpen(false);
          }
        };
        isSection = isSettingsSection;
      } else if (item.path === '/support') {
        isOpen = supportOpen;
        setOpen = (open) => {
          setSupportOpen(open);
          if (open) {
            setContractsOpen(false);
            setSettingsOpen(false);
          }
        };
        isSection = isSupportSection;
      }

      return (
        <Collapsible
          key={item.path}
          open={isOpen}
          onOpenChange={setOpen}
        >
          <CollapsibleTrigger asChild>
            <button
              className={`sidebar-item w-full justify-between ${isSection ? 'bg-gray-100 dark:bg-white/5' : ''}`}
              data-testid={`nav-${item.path.replace('/', '')}`}
            >
              <div className="flex items-center gap-3">
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
              </div>
              <ChevronRight className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pl-4 mt-1 space-y-1">
            {item.submenu.map((subItem) => (
              <Link
                key={subItem.path}
                to={subItem.path}
                className={`sidebar-item text-sm ${isActive(subItem.path) ? 'active' : ''}`}
                data-testid={`nav-${subItem.path.replace(/\//g, '-').slice(1)}`}
              >
                {subItem.icon && <subItem.icon className="w-4 h-4" />}
                <span>{subItem.label}</span>
              </Link>
            ))}
          </CollapsibleContent>
        </Collapsible>
      );
    }

    return (
      <Link
        key={item.path}
        to={item.path}
        className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
        data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
      >
        <item.icon className="w-5 h-5" />
        <span>{item.label}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-background flex">
      <style>{`
        .sidebar-item {
          color: #4b5563;
          border-radius: 0.5rem;
          margin-bottom: 0.25rem;
          padding: 0.75rem 1rem;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          transition: all 0.2s;
        }
        .dark .sidebar-item {
          color: #d1d5db;
        }
        .sidebar-item:hover {
          background-color: #f3f4f6;
          color: #111827;
        }
        .dark .sidebar-item:hover {
          background-color: #444444;
          color: white;
        }
        .sidebar-item.active {
          background-color: var(--theme-accent, #333333) !important;
          color: var(--theme-accent-foreground, #ffffff) !important;
        }
        .dark .sidebar-item.active {
          background-color: var(--theme-accent, #4b5563) !important;
          color: var(--theme-accent-foreground, #ffffff) !important;
        }
        .sidebar-item.active:hover {
          background-color: var(--theme-accent, #333333);
          filter: brightness(1.1);
        }
      `}</style>

      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 sidebar fixed h-full z-40 bg-white dark:bg-[#333333] border-r border-gray-200 dark:border-none shadow-sm">
        <div className="flex flex-col items-center justify-center pt-8 pb-6 px-4">
          <div className="w-40 h-40 rounded-full flex items-center justify-center bg-gray-50 dark:bg-[#444444] mb-4 overflow-hidden border border-gray-100 dark:border-none p-3">
            <img src="/logo.png" alt="Contract Manager" className="w-full h-full object-contain scale-150" />
          </div>
          <h1 className="font-bold text-gray-800 dark:text-white text-lg tracking-tight text-center">Contract Manager</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-1">www.connectedmobile.de</p>
        </div>

        <nav className="flex-1 px-4 py-4 space-y-2 overflow-y-auto">
          {navItems.map(renderNavItem)}
        </nav>

      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setSidebarOpen(false)}
          />
          <aside className="relative w-64 sidebar flex flex-col bg-white dark:bg-[#333333] h-full shadow-2xl">
            <div className="flex flex-col items-center justify-center pt-8 pb-6 px-4 border-b border-gray-100 dark:border-gray-700">
              <div className="w-32 h-32 rounded-full flex items-center justify-center bg-gray-50 dark:bg-[#444444] mb-3 overflow-hidden border border-gray-100 dark:border-none p-3">
                <img src="/logo.png" alt="Contract Manager" className="w-full h-full object-contain scale-150" />
              </div>
              <div className="text-center">
                <h1 className="font-bold text-gray-800 dark:text-white text-lg">Contract Manager</h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">www.connectedmobile.de</p>
              </div>
              <button onClick={() => setSidebarOpen(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <nav className="flex-1 px-3 py-4 space-y-1">
              {navItems.map((item) => {
                if (item.submenu) {
                  let isSection;
                  if (item.path === '/contracts') {
                    isSection = isContractsSection;
                  } else if (item.path === '/settings') {
                    isSection = isSettingsSection;
                  } else if (item.path === '/support') {
                    isSection = isSupportSection;
                  }
                  return (
                    <div key={item.path} className="space-y-1">
                      <div className={`sidebar-item ${isSection ? 'bg-gray-100' : ''}`}>
                        <item.icon className="w-5 h-5" />
                        <span>{item.label}</span>
                      </div>
                      <div className="pl-8 space-y-1">
                        {item.submenu.map((subItem) => (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            onClick={() => setSidebarOpen(false)}
                            className={`sidebar-item text-sm ${isActive(subItem.path) ? 'active' : ''}`}
                          >
                            <span>{subItem.label}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  );
                }

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
                  >
                    <item.icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </nav>

          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen min-w-0 overflow-x-hidden">
        {/* Header */}
        <header className="glass-header sticky top-0 z-30 px-3 lg:px-6 py-2 dark:bg-[#333333] dark:border-none">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-muted dark:text-gray-300 dark:hover:bg-[#444444]"
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>

            <div className="flex items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-lg dark:text-gray-300 dark:hover:bg-[#444444]">
                    <Globe className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="dark:bg-[#444444] dark:border-gray-700">
                  <DropdownMenuItem onClick={() => i18n.changeLanguage('tr')} className="dark:text-white dark:focus:bg-[#555555]">Türkçe</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => i18n.changeLanguage('en')} className="dark:text-white dark:focus:bg-[#555555]">English</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => i18n.changeLanguage('de')} className="dark:text-white dark:focus:bg-[#555555]">Deutsch</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                variant="ghost"
                size="icon"
                onClick={toggleDarkMode}
                className="rounded-lg dark:text-gray-300 dark:hover:bg-[#444444]"
                data-testid="theme-toggle-btn"
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              <Button
                variant="ghost"
                onClick={handleLogout}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
                <span className="hidden md:block text-sm font-medium">{t('nav.logout')}</span>
              </Button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};
